import type { OneUSpec, PanelFormatKey } from "@lib/panelFormat";
import type { KnobId, PartId } from "@lib/parts";
import type { PanelSize } from "@lib/share";
import type { TextFontId } from "@lib/text/textFonts";

interface Translations {
  app: {
    title: string;
    subtitle: string;
    errorFallback: string;
    storageFull: string;
  };
  githubPagesMigration: {
    imported: string;
    failed: string;
    sessionProjectName: string;
    projectNameSuffix: string;
  };
  header: {
    githubAriaLabel: string;
    supportAriaLabel: string;
    supportImageAlt: string;
    etsyLabel: string;
    etsyAriaLabel: string;
    termsLabel: string;
  };
  canvas: {
    hudSuffix: string;
  };
  view3d: {
    modeLabel: string;
    mode2d: string;
    mode3d: string;
    modeSplit: string;
    canvasLabel: string;
    loading: string;
    unavailable: string;
    hud: (thicknessMm: number) => string;
  };
  controls: {
    formatLabel: string;
    rackUnitsOption: (rackUnits: number) => string;
    kosmoOption: string;
    oneUSpecLabel: string;
    oneUSpecOptions: Record<OneUSpec, string>;
    customLabel: string;
    widthHpLabel: string;
    widthCmLabel: string;
    widthMmLabel: string;
    heightMmLabel: string;
    widthHpHint: string;
    tileWidthHpHint: (stepHp: number) => string;
    kosmoWidthCmHint: (stepCm: number) => string;
    widthMmHint: string;
    kosmoWidthMmHint: (stepMm: number) => string;
    customWidthMmHint: string;
    customHeightMmHint: (minMm: number, maxMm: number) => string;
    heightNote: (heightMm: number, formatName: string) => string;
    derivedHeightNote: string;
    customNote: (widthHp: number) => string;
    formatNames: Record<Exclude<PanelFormatKey, "custom">, string>;
  };
  display: {
    grid: string;
    snap: string;
    holes: string;
    dimensions: string;
    hardware: string;
    gridSpacing: string;
    overlaysLabel: string;
    reset: string;
    panelColor: string;
    designColor: string;
  };
  mountingHoles: {
    title: string;
    description: string;
    diameterLabel: string;
    slotLengthLabel: string;
    shapeLabel: string;
    typeCircle: string;
    typeSlot: string;
    close: string;
  };
  elementHoles: {
    title: string;
    description: string;
    enableLabel: string;
    countLabel: string;
    diameterLabel: string;
    offsetLabel: string;
    rotationLabel: string;
    defaultRotationLabel: string;
  };
  palette: {
    title: string;
    subtitle: string;
    clear: string;
    select: string;
    place: string;
    shapeLabelPrefix: string;
    items: Record<
      string,
      {
        label: string;
        description: string;
        color: string;
      }
    >;
  };
  rightPanel: {
    tabsLabel: string;
    display: string;
    properties: string;
    components: string;
    componentsWithCount: (count: number) => string;
  };
  layout: {
    showTools: string;
    showProperties: string;
    closePanel: string;
  };
  components: {
    listLabel: string;
    summary: (count: number, hiddenCount: number) => string;
    showAll: string;
    empty: string;
    hint: string;
    rename: (name: string) => string;
    hide: (name: string) => string;
    show: (name: string) => string;
    lock: (name: string) => string;
    unlock: (name: string) => string;
    remove: (name: string) => string;
  };
  properties: {
    title: string;
    subtitle: string;
    delete: string;
    empty: string;
    multiSelection: (count: number) => string;
    multiSelectionHint: string;
    posX: string;
    posY: string;
    rotation: string;
    diameter: string;
    width: string;
    height: string;
    text: string;
    fontSize: string;
    color: string;
    font: string;
    fontOptions: Record<TextFontId, string>;
    textColorHint: string;
    patternOverlap: string;
    patternOverlapKnockout: string;
    patternOverlapMerge: string;
    patternOverlapHint: string;
    knockoutPadding: string;
    reliefTitle: string;
    reliefHint: string;
    reliefThickness: string;
    reliefPenetration: string;
    textTooSmall: (minSizePt: number) => string;
    textThinStrokes: (strokeMm: number, minStrokeMm: number) => string;
    textMissingCharacters: (characters: string) => string;
    outerDiameter: string;
    outerDepth: string;
    innerDiameter: string;
    innerDepth: string;
    embedDepth: string;
    part: string;
    partOptions: Record<PartId, string>;
    customHole: string;
    customRoundHole: string;
    customRectangularHole: string;
    partHint: (holeMm: string, hardwareMm: string | null) => string;
    useRecommendedHole: (holeMm: string) => string;
    knob: string;
    noKnob: string;
    knobOptions: Record<KnobId, string>;
    crowdedHardware: string;
  };
  selectionTools: {
    title: string;
    copy: string;
    cut: string;
    paste: string;
    duplicate: string;
    delete: string;
    copyKeys: string;
    cutKeys: string;
    pasteKeys: string;
    duplicateKeys: string;
    deleteKeys: string;
    applyKeys: string;
    mirror: string;
    rectangular: string;
    circular: string;
    apply: string;
    cancel: string;
    middleOfPanel: string;
    mirrorPickHint: string;
    mirrorDragHint: string;
    lineOrientation: string;
    lineVertical: string;
    lineHorizontal: string;
    linePositionX: string;
    linePositionY: string;
    keepOriginal: string;
    rectangularHint: string;
    columns: string;
    rows: string;
    spacingX: string;
    spacingY: string;
    circularPickHint: string;
    circularDragHint: string;
    centerX: string;
    centerY: string;
    count: string;
    diameter: string;
    sweep: string;
    rotateCopies: string;
    tooMany: (max: number) => string;
    copyCount: (count: number) => string;
    copied: (count: number) => string;
  };
  referenceImage: {
    title: string;
    positionX: string;
    positionY: string;
    width: string;
    height: string;
    rotation: string;
    opacity: string;
    replace: string;
    remove: string;
    notKept: string;
    calibrate: string;
    calibrationTitle: string;
    calibrationPickFirst: string;
    calibrationPickSecond: string;
    calibrationDistance: string;
    calibrationMeasured: (distanceMm: number) => string;
    calibrationApply: string;
    calibrationCancel: string;
    calibrationInvalid: string;
  };
  svgArtwork: {
    title: string;
    description: string;
    dropLabel: string;
    libraryTitle: string;
    libraryLoading: string;
    libraryEmpty: string;
    invalidFile: string;
    invalidSvg: string;
    libraryError: string;
    cancel: string;
  };
  projects: {
    title: string;
    subtitle: string;
    nameLabel: string;
    editNameLabel: string;
    newProject: string;
    defaultName: string;
    save: string;
    exportJson: string;
    exportPng: string;
    exportSvg: string;
    exportKicadSvg: string;
    exportKicadPcb: string;
    exportStl: string;
    exportMenuLabel: string;
    exportTermsNotice: string;
    exportTermsLink: string;
    orderPrint: string;
    reset: string;
    savedLabel: string;
    load: string;
    delete: string;
    importJson: string;
    importImage: string;
    refresh: string;
    unsavedChanges: string;
    savedPlaceholder: string;
    deleteSelected: (name: string) => string;
    exportFormatShort: Record<"svg" | "png" | "kicadSvg" | "kicadPcb" | "stl", string>;
    messages: {
      saveSuccess: (name: string) => string;
      saveError: (name: string) => string;
      loadSuccess: (name: string) => string;
      loadError: (name: string) => string;
      deleteSuccess: (name: string) => string;
      deleteUndoSuccess: (name: string) => string;
      importSuccess: (filename: string) => string;
      importError: string;
      pngError: string;
      pngSuccess: string;
      jsonExport: string;
      svgExport: string;
      svgError: string;
      kicadSvgExport: string;
      kicadPcbExport: string;
      kicadError: string;
      stlExport: string;
      stlExportWithWarnings: (count: number) => string;
      stlError: string;
      reset: string;
      confirmSaveBeforeNew: string;
      confirmDeleteSelected: (name: string) => string;
      confirmReset: string;
      confirmYes: string;
      confirmNo: string;
    };
    stlDialog: {
      title: string;
      description: string;
      fileNameLabel: string;
      fileNameHint: string;
      thicknessLabel: string;
      thicknessHint: string;
      previewLabel: string;
      previewLoading: string;
      cancel: string;
      confirm: string;
    };
  };
  changelog: {
    buttonLabel: string;
    title: string;
    description: string;
    close: string;
    viewFull: string;
  };
  order: {
    dialogTitle: string;
    dialogDescription: string;
    previewLabel: string;
    previewLoading: string;
    panelFilamentLabel: string;
    detailsFilamentLabel: string;
    detailsFilamentHint: string;
    filamentNames: { white: string; black: string; skyBlue: string };
    widthLabel: string;
    widthValue: (widthHp: number) => string;
    priceLabel: string;
    price: (priceEur: number) => string;
    priceHint: string;
    stepsTitle: string;
    steps: (widthHp: number) => string[];
    issueUnsupportedFormat: string;
    issueTooWide: (widthHp: number, maxWidthHp: number) => string;
    issueSameFilament: string;
    issueTextPrint: (count: number) => string;
    issueCrowdedHardware: (count: number) => string;
    issueHiddenElements: (count: number) => string;
    cancel: string;
    submit: string;
    submitting: string;
    errors: {
      unavailable: string;
      tooLarge: string;
      invalid: string;
      server: string;
      network: string;
    };
    pageTitle: string;
    designTitle: string;
    codeLabel: string;
    codeHint: string;
    copyCode: string;
    copied: string;
    howToTitle: string;
    howToSteps: (widthHp: number) => string[];
    buyCta: string;
    buyUnavailable: string;
    filesTitle: string;
    filesHint: (thicknessMm: number) => string;
    downloadStl: string;
    downloadJson: string;
    downloadError: string;
    versionNote: (version: string, commit: string) => string;
    versionChanged: string;
    backToDesigner: string;
    loading: string;
    loadError: string;
    notFound: string;
  };
  share: {
    button: string;
    dialogTitle: string;
    dialogDescription: string;
    create: string;
    creating: string;
    linkLabel: string;
    copyLink: string;
    copied: string;
    openLink: string;
    close: string;
    cancel: string;
    errors: {
      tooLarge: string;
      invalid: string;
      server: string;
      network: string;
    };
    size: (size: PanelSize) => string;
    cardTagline: string;
    pageEyebrow: string;
    untitled: string;
    previewLoading: string;
    openInEditor: string;
    openInEditorHint: string;
    downloadJson: string;
    designYourOwn: string;
    loading: string;
    notFound: string;
    loadError: string;
    opened: (name: string) => string;
    openError: string;
  };
  shortcuts: {
    shift: string;
    disableSnap: string;
    esc: string;
    cancelPlacement: string;
    deleteKey: string;
    deleteSelection: string;
    undo: string;
    undoShortcut: string;
    redo: string;
    redoShortcut: string;
    copyPaste: string;
    copyPasteShortcut: string;
    duplicate: string;
    duplicateShortcut: string;
    rightClick: string;
    contextMenu: string;
  };
}

/** A size with at most one decimal and no trailing zero: 60.6, 128.5, 10. */
function formatDecimal(value: number): string {
  return String(Math.round(value * 10) / 10);
}

export const enUS: Translations = {
  app: {
    title: "Eurorack Panel Designer",
    subtitle: "Interactive canvas, zoom, pan, selection",
    errorFallback: "An unexpected error occurred. Please reload the page.",
    storageFull:
      "Browser storage is full: recent changes will not survive a reload. Delete saved projects you no longer need or remove the reference image.",
  },
  githubPagesMigration: {
    imported: "Your designs from the previous address (GitHub Pages) were moved to this site.",
    failed:
      "Some of your designs from the previous address could not be moved. Please report it on GitHub.",
    sessionProjectName: "Last session on GitHub Pages",
    projectNameSuffix: "(GitHub Pages)",
  },
  header: {
    githubAriaLabel: "Source code on GitHub",
    supportAriaLabel: "Support the project on Ko-fi",
    supportImageAlt: "Buy me a coffee on Ko-fi",
    etsyLabel: "Etsy",
    etsyAriaLabel: "Open the RatpiSynth Etsy shop",
    termsLabel: "Terms of use",
  },
  canvas: {
    hudSuffix: "Static render",
  },
  view3d: {
    modeLabel: "View",
    mode2d: "2D view",
    mode3d: "3D view",
    modeSplit: "2D and 3D side by side",
    canvasLabel: "3D view of the panel",
    loading: "Loading 3D view…",
    unavailable: "The 3D view needs WebGL, which this browser does not provide.",
    hud: (thicknessMm: number) => `${Number(thicknessMm.toFixed(2))} mm thick · Drag to rotate`,
  },
  controls: {
    formatLabel: "Format",
    rackUnitsOption: (rackUnits) => `${rackUnits}U`,
    kosmoOption: "Kosmo",
    oneUSpecLabel: "1U standard",
    oneUSpecOptions: {
      intellijel: "Intellijel",
      pulpLogic: "Pulp Logic",
    },
    customLabel: "Custom size",
    widthHpLabel: "Width (HP)",
    widthCmLabel: "Width (cm)",
    widthMmLabel: "Width (mm)",
    heightMmLabel: "Height (mm)",
    widthHpHint: "Eurorack units (1 HP = 5.08 mm)",
    tileWidthHpHint: (stepHp) => `Tiles come in multiples of ${stepHp} HP`,
    kosmoWidthCmHint: (stepCm) => `Kosmo widths come in steps of ${stepCm} cm`,
    widthMmHint: "Width to cut, a bit under the HP grid (Doepfer)",
    kosmoWidthMmHint: (stepMm) => `Width to cut, a multiple of ${stepMm} mm`,
    customWidthMmHint: "Any width, in millimeters",
    customHeightMmHint: (minMm, maxMm) => `Any height from ${minMm} to ${maxMm} mm`,
    heightNote: (heightMm, formatName) => `Height ${Number(heightMm.toFixed(2))} mm, ${formatName}`,
    derivedHeightNote:
      "No brand publishes this height: it is the rack unit less the rail lips, as on 3U.",
    customNote: (widthHp) => `Takes ${widthHp} HP on the rails`,
    formatNames: {
      intellijel1u: "Intellijel 1U",
      pulpLogic1u: "Pulp Logic 1U tile",
      rack2u: "2U rails",
      eurorack3u: "Eurorack 3U",
      rack4u: "4U rails",
      kosmo: "Kosmo",
    },
  },
  display: {
    grid: "Grid",
    snap: "Snap to grid",
    holes: "Mounting holes",
    dimensions: "Dimensions",
    hardware: "Knobs and nuts",
    gridSpacing: "Grid spacing (mm)",
    overlaysLabel: "Canvas options",
    reset: "Reset view",
    panelColor: "Panel color",
    designColor: "Design color",
  },
  mountingHoles: {
    title: "Mounting holes",
    description: "Adjust rail hardware size and shape.",
    diameterLabel: "Diameter (mm)",
    slotLengthLabel: "Slot length (mm)",
    shapeLabel: "Shape",
    typeCircle: "Round",
    typeSlot: "Slot",
    close: "Close",
  },
  elementHoles: {
    title: "Element screw holes",
    description: "Automatically add screw holes around the selected element.",
    enableLabel: "Mounting holes",
    countLabel: "Hole count",
    diameterLabel: "Hole diameter (mm)",
    offsetLabel: "Distance from edge (mm)",
    defaultRotationLabel: "Default rotation (°)",
    rotationLabel: "Element rotation",
  },
  selectionTools: {
    title: "Copy, mirror & pattern",
    copy: "Copy",
    cut: "Cut",
    paste: "Paste",
    duplicate: "Duplicate",
    delete: "Delete",
    copyKeys: "Ctrl/Cmd + C",
    cutKeys: "Ctrl/Cmd + X",
    pasteKeys: "Ctrl/Cmd + V",
    duplicateKeys: "Ctrl/Cmd + D",
    deleteKeys: "Delete",
    applyKeys: "Enter",
    mirror: "Mirror",
    rectangular: "Rectangular pattern",
    circular: "Circular pattern",
    apply: "OK",
    cancel: "Cancel",
    middleOfPanel: "Middle of the panel",
    mirrorPickHint:
      "Click the panel where the mirror line goes. It snaps to the middle of the panel and to the centers of the other elements.",
    mirrorDragHint: "Drag the line on the panel to move it. The result shows as you go.",
    lineOrientation: "Mirror line",
    lineVertical: "Vertical (left ↔ right)",
    lineHorizontal: "Horizontal (top ↔ bottom)",
    linePositionX: "Line X (mm)",
    linePositionY: "Line Y (mm)",
    keepOriginal: "Keep the original (off: move it across)",
    rectangularHint:
      "Drag the two arrows on the panel to set the spacing; 5.08 mm is 1 HP. The selection stays in the first cell.",
    columns: "Columns",
    rows: "Rows",
    spacingX: "Spacing X (mm)",
    spacingY: "Spacing Y (mm)",
    circularPickHint:
      "Click the panel to place the center of the circle. It snaps to the centers of the other elements, such as a knob.",
    circularDragHint:
      "Drag the center, or the arrow to change the diameter. 360° spaces the elements evenly; less spreads them over an arc, clockwise.",
    centerX: "Center X (mm)",
    centerY: "Center Y (mm)",
    count: "Count",
    diameter: "Diameter (mm)",
    sweep: "Angle (°)",
    rotateCopies: "Turn copies to follow the circle",
    tooMany: (max: number) => `Up to ${max} copies at once.`,
    copyCount: (count: number) =>
      count === 0 ? "No copy" : count === 1 ? "1 copy" : `${count} copies`,
    copied: (count: number) => (count === 1 ? "1 element copied" : `${count} elements copied`),
  },
  referenceImage: {
    title: "Reference image",
    positionX: "Position X (mm)",
    positionY: "Position Y (mm)",
    width: "Width (mm)",
    height: "Height (mm)",
    rotation: "Rotation (°)",
    opacity: "Opacity",
    replace: "Replace the image",
    remove: "Remove the image",
    notKept:
      "This reference image is too large to be kept after a reload. The rest of your design is still saved.",
    calibrate: "Calibrate the scale from two points",
    calibrationTitle: "Calibrate the image",
    calibrationPickFirst: "Click the first point of a known distance on the image.",
    calibrationPickSecond: "Click the second point.",
    calibrationDistance: "Real distance (mm)",
    calibrationMeasured: (distanceMm) =>
      `Measured on the image: ${Number(distanceMm.toFixed(2))} mm. Click again to pick other points.`,
    calibrationApply: "Scale",
    calibrationCancel: "Cancel the calibration (Esc)",
    calibrationInvalid: "Enter a distance above 0 mm.",
  },
  palette: {
    title: "Palette",
    subtitle: "Pick an element to place",
    clear: "Cancel placement (Esc)",
    select: "Select",
    place: "Place…",
    shapeLabelPrefix: "Shape",
    items: {
      jack: {
        label: "Jack",
        description: "Input/output: 3.5 mm, 1/4 in or banana",
        color: "#38bdf8",
      },
      potentiometer: {
        label: "Knob",
        description: "Rotary potentiometer",
        color: "#f472b6",
      },
      switch: {
        label: "Switch",
        description: "Toggle switch",
        color: "#facc15",
      },
      led: {
        label: "LED",
        description: "Indicator LED",
        color: "#f87171",
      },
      label: {
        label: "Text",
        description: "Raised text in the design color",
        color: "#f8fafc",
      },
      rectangle: {
        label: "Rectangle",
        description: "Generic rectangular cutout",
        color: "#4ade80",
      },
      oval: {
        label: "Oval",
        description: "Ellipse cutout",
        color: "#c084fc",
      },
      slot: {
        label: "Slot",
        description: "Capsule / slotted cutout",
        color: "#fb923c",
      },
      triangle: {
        label: "Triangle",
        description: "Triangular cutout",
        color: "#22d3ee",
      },
      insert: {
        label: "Insert",
        description: "Press-fit insert with inner hole",
        color: "#f59e0b",
      },
      svgArtwork: {
        label: "SVG",
        description: "Decorative vector artwork",
        color: "#f8fafc",
      },
    },
  },
  rightPanel: {
    tabsLabel: "Panel settings",
    display: "Display",
    properties: "Properties",
    components: "Components",
    componentsWithCount: (count) => `Components (${count})`,
  },
  layout: {
    showTools: "Tools",
    showProperties: "Properties",
    closePanel: "Close",
  },
  components: {
    listLabel: "Placed components",
    summary: (count, hiddenCount) =>
      `${count} component${count === 1 ? "" : "s"}${hiddenCount ? ` · ${hiddenCount} hidden` : ""}`,
    showAll: "Show all hidden components",
    empty: "No components yet. Pick one in the palette, then click on the panel to place it.",
    hint: "Shift-click to select several. Double-click a name to rename it. Hidden components are left out of the 3D view, exports and orders; locked ones cannot be moved on the canvas.",
    rename: (name) => `Rename ${name}`,
    hide: (name) => `Hide ${name}`,
    show: (name) => `Show ${name}`,
    lock: (name) => `Lock ${name}`,
    unlock: (name) => `Unlock ${name}`,
    remove: (name) => `Delete ${name}`,
  },
  properties: {
    title: "Properties",
    subtitle: "Selection",
    delete: "Delete",
    empty: "Select an element to view its properties.",
    multiSelection: (count: number) => `${count} elements selected`,
    multiSelectionHint:
      "Multi-selection is active. Drag elements on the canvas to move the group, copy, mirror or repeat it below, or press Delete to remove it.",
    posX: "Position X (mm)",
    posY: "Position Y (mm)",
    rotation: "Rotation (°)",
    diameter: "Diameter (mm)",
    width: "Width (mm)",
    height: "Height (mm)",
    text: "Text",
    fontSize: "Size (pt)",
    color: "Color",
    font: "Font",
    fontOptions: {
      roboto: "Roboto Bold · clean sans",
      barlowCondensed: "Barlow Condensed Bold · condensed",
      jetbrainsMono: "JetBrains Mono Bold · monospace",
      michroma: "Michroma · wide, technical",
      orbitron: "Orbitron Bold · geometric display",
    },
    textColorHint:
      "The design color, the panel's second print color: it also applies to every SVG pattern.",
    patternOverlap: "Over SVG patterns",
    patternOverlapKnockout: "Clear the pattern around the text",
    patternOverlapMerge: "Merge into the pattern",
    patternOverlapHint:
      "Clearing keeps the text readable. Merged text joins the pattern at the same height, even where that makes it harder to read.",
    knockoutPadding: "Clearance (mm)",
    reliefTitle: "Relief",
    reliefHint: "Shared by every text and SVG pattern, which print at the same height.",
    reliefThickness: "Thickness (mm)",
    reliefPenetration: "Sunk into panel (mm)",
    textTooSmall: (minSizePt: number) => `Text under ${minSizePt} pt is hard to print.`,
    textThinStrokes: (strokeMm: number, minStrokeMm: number) =>
      `Strokes are about ${strokeMm.toFixed(2)} mm wide and may not print under ${minStrokeMm} mm: use a larger size or a bolder font.`,
    textMissingCharacters: (characters: string) => `Not in this font, so left out: ${characters}`,
    outerDiameter: "Outer diameter (mm)",
    outerDepth: "Outer depth (mm)",
    innerDiameter: "Inner diameter (mm)",
    innerDepth: "Inner depth (mm)",
    embedDepth: "Embed depth (mm)",
    part: "Part",
    partOptions: {
      thonkiconn: "Thonkiconn 3.5 mm · PJ398SM, WQP518MA",
      switchcraft112a: "Switchcraft 1/4 in · 112A, 11, 12A",
      reanNys229: "Rean 1/4 in · NYS229, NYS230",
      johnsonBanana: "Johnson 4 mm banana · 108-09xx-001",
      taydaBanana: "Tayda 4 mm banana · J072, Hirschmann BIL 20",
      alpha9mm: "Alpha 9 mm pot · RD901F",
      bournsPec11r: "Bourns PEC11R encoder",
      dailywellSubMiniToggle: "Dailywell sub-mini toggle · 2MS",
      dailywellMiniToggle: "Dailywell mini toggle · 1MS",
      led3mm: "3 mm LED",
      led5mm: "5 mm LED",
    },
    customHole: "Custom hole",
    customRoundHole: "Custom round hole",
    customRectangularHole: "Custom rectangular hole",
    partHint: (holeMm: string, hardwareMm: string | null) =>
      hardwareMm
        ? `Recommended hole Ø${holeMm} mm; its nut, washer or head covers Ø${hardwareMm} mm on the front.`
        : `Recommended hole Ø${holeMm} mm.`,
    useRecommendedHole: (holeMm: string) => `Use Ø${holeMm} mm`,
    knob: "Knob",
    noKnob: "No knob",
    knobOptions: {
      thonkTallTrimmerTopper: "Thonk Tall Trimmer Topper",
      davies1900h: "Davies 1900H clone",
      roganPt1ps: "Rogan PT-1PS, small",
      roganPt2ps: "Rogan PT-2PS, medium",
      roganPt3ps: "Rogan PT-3PS, large",
    },
    crowdedHardware:
      "Too close to another component: a knob, nut, washer or socket head overlaps. Move them apart.",
  },
  svgArtwork: {
    title: "Add SVG",
    description: "Load a monochrome vector mark for the panel surface.",
    dropLabel: "Drop SVG here or choose a file",
    libraryTitle: "Library",
    libraryLoading: "Loading library…",
    libraryEmpty: "No SVG assets in the library yet.",
    invalidFile: "Choose an SVG file.",
    invalidSvg: "Unable to read this SVG.",
    libraryError: "Unable to load this library SVG.",
    cancel: "Cancel",
  },
  projects: {
    title: "Projects",
    subtitle: "Local save + export",
    nameLabel: "Project name",
    editNameLabel: "Rename the project",
    newProject: "New project",
    defaultName: "Untitled Project",
    save: "Save the project",
    exportJson: "Export JSON",
    exportPng: "Export PNG",
    exportSvg: "Export SVG",
    exportKicadSvg: "Export KiCad Edge SVG",
    exportKicadPcb: "Export KiCad PCB",
    exportStl: "Export STL",
    exportMenuLabel: "Choose export format",
    exportTermsNotice: "For personal use: do not sell these files, nor panels made from them.",
    exportTermsLink: "Terms of use",
    orderPrint: "Order this panel",
    reset: "Reset design",
    savedLabel: "Saved projects",
    load: "Open the selected project",
    delete: "Delete",
    importJson: "Import a JSON design",
    importImage: "Import a reference image",
    refresh: "Refresh",
    unsavedChanges: "Unsaved changes",
    savedPlaceholder: "Saved projects…",
    deleteSelected: (name) => `Delete the saved project "${name}"`,
    exportFormatShort: {
      svg: "SVG",
      png: "PNG",
      kicadSvg: "KiCad SVG",
      kicadPcb: "KiCad PCB",
      stl: "STL",
    },
    messages: {
      saveSuccess: (name: string) => `Project "${name}" saved.`,
      saveError: (name: string) =>
        `Unable to save "${name}": browser storage is full. Delete projects you no longer need and try again.`,
      loadSuccess: (name: string) => `Project "${name}" loaded.`,
      loadError: (name: string) => `Unable to load "${name}".`,
      deleteSuccess: (name: string) => `Project "${name}" deleted.`,
      deleteUndoSuccess: (name: string) => `Project "${name}" restored.`,
      importSuccess: (filename: string) => `Imported from "${filename}".`,
      importError: "Invalid JSON import.",
      pngError: "Canvas not available for PNG export.",
      pngSuccess: "PNG export created.",
      jsonExport: "JSON export created.",
      svgExport: "SVG export created.",
      svgError: "Failed to generate SVG export.",
      kicadSvgExport: "KiCad Edge.Cuts SVG created.",
      kicadPcbExport: "KiCad PCB export created.",
      kicadError: "Failed to generate KiCad export.",
      stlExport: "STL export created.",
      stlExportWithWarnings: (count: number) =>
        `STL export created. ${count} SVG pattern${count === 1 ? " or text is" : "s or texts are"} missing or incomplete in the model.`,
      stlError: "Failed to generate STL export.",
      reset: "Design reset.",
      confirmSaveBeforeNew: "Save current project before creating a new one?",
      confirmDeleteSelected: (name: string) => `Delete saved project "${name}"?`,
      confirmReset: "Reset the current design?",
      confirmYes: "Yes",
      confirmNo: "No",
    },
    stlDialog: {
      title: "Export STL",
      description: "Set the panel thickness in millimeters for the 3D model.",
      fileNameLabel: "File name",
      fileNameHint: "The .stl extension is added automatically.",
      thicknessLabel: "Panel thickness (mm)",
      thicknessHint: "Default is 2 mm.",
      previewLabel: "Preview",
      previewLoading: "Loading preview…",
      cancel: "Cancel",
      confirm: "Export STL",
    },
  },
  changelog: {
    buttonLabel: "What's new",
    title: "Changelog",
    description: "Here is what changed recently in Eurorack Panel Designer.",
    close: "Close",
    viewFull: "Open full changelog",
  },
  order: {
    dialogTitle: "Order this panel",
    dialogDescription:
      "We 3D print your panel in two colors and ship it to you. Pick the colors, get a design code, then buy the panel on Etsy with that code.",
    previewLabel: "Print preview",
    previewLoading: "Loading the 3D preview…",
    panelFilamentLabel: "Panel color",
    detailsFilamentLabel: "Text and pattern color",
    detailsFilamentHint: "Text and SVG patterns are printed in relief, in this color.",
    filamentNames: { white: "White", black: "Black", skyBlue: "Sky blue" },
    widthLabel: "Width",
    widthValue: (widthHp) => `${widthHp} HP`,
    priceLabel: "Price on Etsy",
    price: (priceEur) =>
      new Intl.NumberFormat("en", {
        style: "currency",
        currency: "EUR",
        trailingZeroDisplay: "stripIfInteger",
      }).format(priceEur),
    priceHint: "Shipping is added on Etsy.",
    stepsTitle: "How ordering works",
    steps: (widthHp) => [
      "Get your design code here.",
      `On Etsy, choose the width ${widthHp} HP.`,
      "Paste the code in the personalization field and complete the purchase.",
    ],
    issueUnsupportedFormat:
      "Only 3U Eurorack panels can be ordered for now. Set the format back to 3U in the width box to order this design.",
    issueTooWide: (widthHp, maxWidthHp) =>
      `Panels up to ${maxWidthHp} HP can be ordered. This one is ${widthHp} HP.`,
    issueSameFilament:
      "The panel, text and patterns share one color: text and patterns will not stand out.",
    issueTextPrint: (count) =>
      count === 1
        ? "A text may not print well: see the warning in its properties."
        : `${count} texts may not print well: see the warnings in their properties.`,
    issueCrowdedHardware: (count) =>
      `${count} components are too close together: their knobs, nuts or washers overlap (in red on the panel).`,
    issueHiddenElements: (count) =>
      count === 1
        ? "A hidden component will not be printed. Show it in the Components tab to include it."
        : `${count} hidden components will not be printed. Show them in the Components tab to include them.`,
    cancel: "Cancel",
    submit: "Get my design code",
    submitting: "Saving your design…",
    errors: {
      unavailable: "Ordering is not available on this site right now.",
      tooLarge: "This design is too large to send. Try smaller SVG artwork.",
      invalid: "This design could not be sent. Reload the page and try again.",
      server: "Your design could not be saved. Try again in a moment.",
      network: "Could not reach the server. Check your connection and try again.",
    },
    pageTitle: "Your panel is ready to order",
    designTitle: "Your panel design",
    codeLabel: "Design code",
    codeHint:
      "This code links your Etsy order to this design. You can come back to this page later.",
    copyCode: "Copy",
    copied: "Copied",
    howToTitle: "Order on Etsy",
    howToSteps: (widthHp) => [
      "Open the Etsy listing with the button below: it copies the code for you.",
      `Choose the width ${widthHp} HP.`,
      "Paste the code in the personalization field, then complete the purchase.",
    ],
    buyCta: "Buy on Etsy",
    buyUnavailable: "Ordering is not available on this site right now.",
    filesTitle: "Print files",
    filesHint: (thicknessMm) =>
      `The STL is built at ${thicknessMm} mm, as the panel is printed. The JSON opens in the designer with "Import JSON".`,
    downloadStl: "Download STL",
    downloadJson: "Download design (JSON)",
    downloadError: "The file could not be built.",
    versionNote: (version, commit) =>
      `Designed with version ${version || "unknown"}${commit ? ` (${commit.slice(0, 7)})` : ""}.`,
    versionChanged: "The designer has changed since: the STL is built with the current version.",
    backToDesigner: "Back to the designer",
    loading: "Loading the design…",
    loadError: "This design could not be loaded. Try again in a moment.",
    notFound: "No design matches this code. Check the code and try again.",
  },
  share: {
    button: "Share a link",
    dialogTitle: "Share this panel",
    dialogDescription:
      "Creating a link uploads a copy of this design. Anyone with the link can see it and open their own copy in the editor; your saved projects stay in your browser. Hidden components are left out.",
    create: "Create link",
    creating: "Creating link…",
    linkLabel: "Link",
    copyLink: "Copy link",
    copied: "Copied",
    openLink: "Open",
    close: "Close",
    cancel: "Cancel",
    errors: {
      tooLarge: "This design is too large to share.",
      invalid: "This design could not be shared.",
      server: "The link could not be created. Try again in a moment.",
      network: "The server could not be reached. Check your connection and try again.",
    },
    size: (size) => {
      switch (size.kind) {
        case "eurorack":
          return size.rackUnits === 3
            ? `${size.widthHp} HP`
            : `${size.widthHp} HP, ${size.rackUnits}U`;
        case "kosmo":
          return `Kosmo, ${formatDecimal(size.widthCm)} cm`;
        case "custom":
          return `${formatDecimal(size.widthMm)} × ${formatDecimal(size.heightMm)} mm`;
      }
    },
    cardTagline: "Made with Eurorack Panel Designer",
    pageEyebrow: "Shared panel",
    untitled: "Untitled panel",
    previewLoading: "Loading the 3D preview…",
    openInEditor: "Open in the editor",
    openInEditorHint:
      "Opens your own copy in the editor, in your browser. The design you were working on is saved first.",
    downloadJson: "Download design (JSON)",
    designYourOwn: "Design your own panel",
    loading: "Loading the design…",
    notFound: "No design is shared under this link.",
    loadError: "This design could not be loaded. Try again in a moment.",
    opened: (name) => `Opened the shared design "${name}".`,
    openError: "The shared design could not be opened.",
  },
  shortcuts: {
    shift: "Shift",
    disableSnap: "Hold to disable snap",
    esc: "Esc",
    cancelPlacement: "Cancel placement",
    deleteKey: "Delete",
    deleteSelection: "Delete selection",
    undo: "Undo",
    undoShortcut: "Ctrl/Cmd + Z",
    redo: "Redo",
    redoShortcut: "Ctrl/Cmd + Shift + Z",
    copyPaste: "Copy, cut, paste",
    copyPasteShortcut: "Ctrl/Cmd + C, X, V",
    duplicate: "Duplicate",
    duplicateShortcut: "Ctrl/Cmd + D",
    rightClick: "Right click",
    contextMenu: "Copy, mirror, pattern",
  },
};
